import type { QuestionStat } from '~/gql/questions/questions'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table'
import TablePagination from '~/pages/backoffice/TablePagination'
import { useClientPagination } from '~/pages/backoffice/use-client-pagination'
import { expectedAnswer, SCORE_BRACKETS } from './question-display'

const percent = (value: number | null | undefined) => value == null ? '—' : `${Math.round(value)} %`

/**
 * Une ligne par question du module, dans son ordre (#159), paginé (10 par défaut,
 * #198). `filtersKey` ramène en page 1 quand les filtres de l'écran changent.
 */
function QuestionsTable({ questions, filtersKey }: { questions: QuestionStat[], filtersKey: string }) {
  const { t } = useTranslation()
  const { pageRows, pagination } = useClientPagination(questions, filtersKey)
  // Numéro dans le module, pas dans la page : la question 11 reste la 11e.
  const offset = (pagination.page - 1) * pagination.pageSize

  return (
    <div>
      <div className="rounded-md border">
        <Table aria-label={t('questions.tableLabel')}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('questions.column.question')}</TableHead>
              <TableHead>{t('questions.column.expected')}</TableHead>
              <TableHead className="text-right">{t('questions.column.respondents')}</TableHead>
              <TableHead className="text-right">{t('questions.column.correctRate')}</TableHead>
              <TableHead className="text-right">{t('questions.column.averageScore')}</TableHead>
              <TableHead>{t('questions.column.brackets')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageRows.map((question, i) => (
              <TableRow key={question.interactionId}>
                <TableCell className="max-w-sm whitespace-normal">
                  <span className="text-muted-foreground">{`${offset + i + 1}. `}</span>
                  {question.description ?? question.interactionId}
                </TableCell>
                <TableCell className="whitespace-normal font-mono text-xs">{expectedAnswer(question.correctResponses) || '—'}</TableCell>
                <TableCell className="text-right">{question.respondents}</TableCell>
                <TableCell className="text-right">{percent(question.correctRate == null ? null : question.correctRate * 100)}</TableCell>
                <TableCell className="text-right">{percent(question.averageScore)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {question.buckets.map((count, bucket) => t('questions.bracket', { range: SCORE_BRACKETS[bucket], count })).join(' · ')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination {...pagination} />
    </div>
  )
}

export default QuestionsTable
